// Shared feedback primitives: banners, spinner, skeletons, and empty/error
// state blocks. Used across the public flow and admin to handle loading, empty
// (no slots), and error states consistently. Implements: architecture.md v1.0.

import type { JSX, ReactNode } from 'react';

export function Banner({
  kind,
  children,
}: {
  // 'notice' is an orange informational callout (e.g. the front-buffer delayed
  // start), warmer than the amber 'warn' used for recoverable load errors.
  kind: 'error' | 'ok' | 'warn' | 'notice';
  children: ReactNode;
}): JSX.Element {
  const cls =
    kind === 'error'
      ? 'banner-err'
      : kind === 'ok'
        ? 'banner-ok'
        : kind === 'notice'
          ? 'banner-notice'
          : 'banner-warn';
  return (
    <div className={`banner ${cls}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span>{children}</span>
    </div>
  );
}

export function Spinner(): JSX.Element {
  return <div className="spinner" role="status" aria-label="Loading" />;
}

export function StateBlock({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="state">
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action ? <div style={{ marginTop: 'var(--space-4)' }}>{action}</div> : null}
    </div>
  );
}

/** A grid of shimmering placeholders sized for booking-type cards. */
export function CardSkeletons({ count = 3 }: { count?: number }): JSX.Element {
  return (
    <div className="type-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton skel-card" />
      ))}
    </div>
  );
}

/** A grid of shimmering placeholders sized for time slots. */
export function SlotSkeletons({ count = 8 }: { count?: number }): JSX.Element {
  return (
    <div className="slot-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton skel-slot" />
      ))}
    </div>
  );
}
